function clone (value) {
  return JSON.parse(JSON.stringify(value))
}

function formatCurrency (value) {
  const normalizedValue = Number(value) || 0

  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(normalizedValue)
}

function formatFetchTimestamp () {
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date())
}

function firstNonEmpty (values, fallback = '') {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }

  return fallback
}

function normalizeComparable (value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

function parseOptionalNumber (value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null
  }

  const parsedValue = Number(value)
  return Number.isNaN(parsedValue) ? null : parsedValue
}

function getSessionContact (sessionUser, contactType) {
  if (!sessionUser || !Array.isArray(sessionUser.information)) {
    return ''
  }

  const contact = sessionUser.information.find(item => item && item.idContactType === contactType)
  return contact ? firstNonEmpty([contact.desContactInformation]) : ''
}

function getPrimaryProgram (sessionUser) {
  if (!sessionUser || !Array.isArray(sessionUser.programs) || sessionUser.programs.length === 0) {
    return {}
  }

  return sessionUser.programs[0] || {}
}

function getRecordValue (record, keys = [], fallback = '') {
  if (!record || typeof record !== 'object') {
    return fallback
  }

  return firstNonEmpty(keys.map(key => record[key]), fallback)
}

function getProgramNameFromCatalog (context = {}, programCode = '') {
  const normalizedProgramCode = normalizeComparable(programCode)
  if (!normalizedProgramCode) {
    return ''
  }

  const catalogs = []

  if (Array.isArray(context.programCatalog)) {
    catalogs.push(...context.programCatalog)
  }

  if (Array.isArray(context.programCodeCatalog)) {
    catalogs.push(...context.programCodeCatalog)
  }

  const matchedProgram = catalogs.find(item => {
    const catalogCode = firstNonEmpty([
      item && item.id,
      item && item.Id,
      item && item.codProgram,
      item && item.CodProgram,
      item && item.Hom_Banner,
      item && item.HomBanner,
    ])

    return normalizeComparable(catalogCode) === normalizedProgramCode
  })

  return firstNonEmpty([
    matchedProgram && matchedProgram.value,
    matchedProgram && matchedProgram.Value,
    matchedProgram && matchedProgram.nameProgram,
    matchedProgram && matchedProgram.NameProgram,
    matchedProgram && matchedProgram.Nam_Program,
  ])
}

function getPaymentOrderProgramCandidates (context = {}) {
  const inscriptionStudent = context.inscriptionStudent || {}
  const inscriptionAccess = context.inscriptionAccess || {}
  const programCode = firstNonEmpty([
    getRecordValue(inscriptionStudent, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
    getRecordValue(inscriptionAccess, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
  ])
  const catalogProgramName = getProgramNameFromCatalog(context, programCode)

  return [
    programCode,
    catalogProgramName,
    getRecordValue(inscriptionStudent, ['program', 'Program']),
    getRecordValue(inscriptionAccess, ['program', 'Program']),
  ].filter(Boolean)
}

function matchesPaymentOrderProgram (orderProgram, candidates = []) {
  const normalizedOrderProgram = normalizeComparable(orderProgram)
  if (!normalizedOrderProgram) {
    return false
  }

  const normalizedCandidates = candidates
    .map(candidate => normalizeComparable(candidate))
    .filter(Boolean)

  if (normalizedCandidates.some(candidate => candidate === normalizedOrderProgram)) {
    return true
  }

  return normalizedCandidates.some(candidate => {
    if (candidate.length < 8 || normalizedOrderProgram.length < 8) {
      return false
    }

    return candidate.indexOf(normalizedOrderProgram) !== -1 ||
      normalizedOrderProgram.indexOf(candidate) !== -1
  })
}

function buildPersonName (record) {
  return firstNonEmpty([
    [getRecordValue(record, ['firstPerson', 'FirstPerson']), getRecordValue(record, ['lastName', 'LastName'])].filter(Boolean).join(' '),
  ])
}

function buildStudentProfile (context = {}) {
  const sessionUser = context.sessionUser || {}
  const inscriptionStudent = context.inscriptionStudent || {}
  const inscriptionAccess = context.inscriptionAccess || {}
  const paymentOrder = context.paymentOrder || {}
  const primaryProgram = getPrimaryProgram(sessionUser)
  const inscriptionName = buildPersonName(inscriptionStudent)
  const accessName = buildPersonName(inscriptionAccess)
  const programCode = firstNonEmpty([
    getRecordValue(paymentOrder, ['programCode', 'ProgramCode']),
    getRecordValue(inscriptionStudent, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
    getRecordValue(inscriptionAccess, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
  ])
  const catalogProgramName = getProgramNameFromCatalog(context, programCode)

  return {
    name: firstNonEmpty([
      inscriptionName,
      accessName,
      [sessionUser && sessionUser.namPerson, sessionUser && sessionUser.lastNamePerson].filter(Boolean).join(' '),
      sessionUser && sessionUser.fullName,
      sessionUser && sessionUser.name,
    ], 'Estudiante Ibero'),
    code: firstNonEmpty([
      getRecordValue(paymentOrder, ['client', 'Cliente']),
      getRecordValue(inscriptionStudent, ['idBanner', 'IdBanner']),
      getRecordValue(inscriptionAccess, ['idBanner', 'IdBanner']),
      sessionUser && sessionUser.idBanner,
      sessionUser && sessionUser.IdBanner,
      sessionUser && sessionUser.numDocument,
    ], 'Sin identificación'),
    documentType: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['typeDocument', 'TypeDocument']),
      getRecordValue(inscriptionStudent, ['idTypeDocument', 'IdTypeDocument']),
    ], 'Sin información disponible'),
    documentNumber: firstNonEmpty([
      getRecordValue(paymentOrder, ['identification', 'Identificacion', 'Identificación']),
      getRecordValue(inscriptionStudent, ['documentNumber', 'DocumentNumber']),
      getRecordValue(inscriptionAccess, ['documentNumber', 'DocumentNumber']),
      sessionUser && sessionUser.numDocument,
      sessionUser && sessionUser.NumDocument,
    ], 'Sin información disponible'),
    program: firstNonEmpty([
      catalogProgramName,
      getRecordValue(paymentOrder, ['program', 'Program']),
      getRecordValue(inscriptionStudent, ['program', 'Program']),
      getRecordValue(inscriptionAccess, ['program', 'Program']),
      sessionUser && sessionUser.nameProgram,
      sessionUser && sessionUser.NameProgram,
      primaryProgram.nameProgram,
      primaryProgram.NameProgram,
      primaryProgram.program,
      primaryProgram.Program,
      primaryProgram.namProgram,
    ], 'Sin información disponible'),
    level: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['typeProgram', 'TypeProgram']),
      getRecordValue(inscriptionAccess, ['typeProgram', 'TypeProgram']),
      sessionUser && sessionUser.level,
      sessionUser && sessionUser.Level,
      sessionUser && sessionUser.nivel,
      primaryProgram.level,
      primaryProgram.Level,
      primaryProgram.nivel,
    ], 'Sin información disponible'),
    faculty: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['faculty', 'Faculty']),
      getRecordValue(inscriptionAccess, ['faculty', 'Faculty']),
      sessionUser && sessionUser.faculty,
      sessionUser && sessionUser.Faculty,
      primaryProgram.faculty,
      primaryProgram.Faculty,
      primaryProgram.school,
      primaryProgram.School,
      primaryProgram.facultad,
    ], 'Sin información disponible'),
    campus: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['operator', 'Operator']),
      [getRecordValue(inscriptionStudent, ['city', 'City']), getRecordValue(inscriptionStudent, ['state', 'State'])].filter(Boolean).join(', '),
      sessionUser && sessionUser.campus,
      sessionUser && sessionUser.Campus,
      primaryProgram.campus,
      primaryProgram.Campus,
      sessionUser && sessionUser.nameResidenceCity,
      sessionUser && sessionUser.NameResidenceCity,
    ], 'Sin información disponible'),
    email: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['emailStu', 'EmailStu']),
      getRecordValue(inscriptionStudent, ['emailPers', 'EmailPers']),
      getSessionContact(sessionUser, 3),
      sessionUser && sessionUser.emailCorporative,
      sessionUser && sessionUser.EmailCorporative,
      sessionUser && sessionUser.email,
      sessionUser && sessionUser.unique_name,
    ], 'Sin información disponible'),
    phone: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['cellPhone', 'CellPhone']),
      getSessionContact(sessionUser, 6),
    ], 'Sin información disponible'),
    address: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['address', 'Address']),
      getSessionContact(sessionUser, 4),
    ], 'Sin información disponible'),
    city: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['city', 'City']),
      sessionUser && sessionUser.nameResidenceCity,
      sessionUser && sessionUser.NameResidenceCity,
    ], 'Sin información disponible'),
    state: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['state', 'State']),
    ], 'Sin información disponible'),
  }
}

function buildCallData (context = {}) {
  const activeGrade = context.activeGrade || {}
  const inscriptionStudent = context.inscriptionStudent || {}
  const inscriptionAccess = context.inscriptionAccess || {}
  const paymentOrder = context.paymentOrder || {}

  return {
    name: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['information', 'Information']),
      getRecordValue(inscriptionAccess, ['information', 'Information']),
      getRecordValue(activeGrade, ['Information', 'information']),
      DEFAULT_CALL.name,
    ], DEFAULT_CALL.name),
    period: firstNonEmpty([
      getRecordValue(paymentOrder, ['period', 'Period']),
      getRecordValue(inscriptionStudent, ['period_Grade', 'Period_Grade']),
      getRecordValue(inscriptionAccess, ['period_Grade', 'Period_Grade']),
      getRecordValue(activeGrade, ['Period_Grade', 'period_Grade']),
      DEFAULT_CALL.period,
    ], DEFAULT_CALL.period),
    registrationDate: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['dateCreated', 'DateCreated']),
      getRecordValue(inscriptionAccess, ['dat_StartInscription', 'dat_StartInscription']),
      getRecordValue(activeGrade, ['dat_StartInscription', 'dat_StartInscription']),
      DEFAULT_CALL.registrationDate,
    ], DEFAULT_CALL.registrationDate),
    ceremonyDate: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['date_Grade', 'Date_Grade']),
      getRecordValue(activeGrade, ['Date_Grade', 'date_Grade']),
      DEFAULT_CALL.ceremonyDate,
    ], DEFAULT_CALL.ceremonyDate),
    ceremonySite: firstNonEmpty([
      getRecordValue(inscriptionStudent, ['operator', 'Operator']),
      DEFAULT_CALL.ceremonySite,
    ], DEFAULT_CALL.ceremonySite),
  }
}

const DEFAULT_CALL = {
  name: 'Proceso de grados',
  period: '2026-1',
  registrationDate: '2026-03-05',
  ceremonyDate: '2026-06-21',
  ceremonySite: 'Auditorio Mayor, sede Bogotá',
}

const FINANCIAL_PORTAL_URL = 'https://icebergprod.ibero.edu.co/iceberg-pf/'

const DOCUMENT_FIELD_DEFINITIONS = [
  {
    id: 'identity-document',
    name: 'Documento de identidad',
    keys: ['idFileDocument', 'IdFileDocument'],
  },
  {
    id: 'saber-pro',
    name: 'Saber Pro',
    keys: ['idFileSaberPro', 'IdFileSaberPro'],
  },
  {
    id: 'bachiller-diploma',
    name: 'Diploma o acta de bachiller',
    keys: ['idFileBachiller', 'IdFileBachiller'],
  },
  {
    id: 'saber-11',
    name: 'Saber 11',
    keys: ['idFileSaber11', 'IdFileSaber11'],
  },
  {
    id: 'photo',
    name: 'Fotografía',
    keys: ['idFilePhoto', 'IdFilePhoto'],
  },
  {
    id: 'pregrado-diploma',
    name: 'Diploma de pregrado',
    keys: ['idFileDipPre', 'IdFileDipPre'],
  },
]

function hasDocumentFile (value) {
  return value !== null && value !== undefined && String(value).trim() !== ''
}

function buildDocuments (documentsSource = {}) {
  if (!documentsSource || typeof documentsSource !== 'object') {
    return []
  }

  return DOCUMENT_FIELD_DEFINITIONS.reduce((documents, definition) => {
    const documentId = getRecordValue(documentsSource, definition.keys)

    if (!hasDocumentFile(documentId)) {
      return documents
    }

    documents.push({
      id: definition.id,
      name: definition.name,
      required: true,
      status: 'CARGADO',
      fileId: documentId,
      note: 'Documento registrado dentro de la inscripcion del proceso de grados.',
    })

    return documents
  }, [])
}

const DEFAULT_BILLING = {
  paymentOrder: {
    status: 'Pendiente',
    hasMatch: false,
    isPaid: false,
    client: '',
    identification: '',
    orderNumber: null,
    document: '',
    programCode: '',
    period: '',
    program: '',
    amount: 0,
    discountAmount: 0,
    paidAmount: null,
    updatedAt: '',
    paymentMethod: '',
    portalUrl: FINANCIAL_PORTAL_URL,
    note: 'La orden de pago se reflejará cuando exista un registro asociado al programa del proceso.',
  },
}

const POSTGRAD_HOLD_CODES_TO_HIDE = ['IM', 'SP']

const APPROVAL_DEFINITIONS = {
  AA: {
    id: 'academic-approval',
    name: 'Aval académico',
    owner: 'Gestión académica',
  },
  IV: {
    id: 'research-approval',
    name: 'Aval de investigación',
    owner: 'Investigación',
  },
  BI: {
    id: 'library-clearance',
    name: 'Paz y salvo de biblioteca',
    owner: 'Biblioteca',
  },
  CT: {
    id: 'financial-clearance',
    name: 'Paz y salvo de cartera',
    owner: 'Cartera',
  },
  EG: {
    id: 'graduates-office',
    name: 'Validación de egresados',
    owner: 'Egresados',
  },
  IM: {
    id: 'imf-test',
    name: 'Validación Pruebas IMF',
    owner: 'Pruebas IMF',
  },
  SP: {
    id: 'saber-pro',
    name: 'Validación Saber Pro',
    owner: 'Saber Pro',
  },
}

function mapApprovalStatus (estado) {
  const normalizedStatus = firstNonEmpty([estado]).toUpperCase()

  if (normalizedStatus === 'OK') {
    return 'Aprobado'
  }

  if (normalizedStatus === 'PENDIENTE') {
    return 'Pendiente'
  }

  if (normalizedStatus === 'SIN_REGISTRO') {
    return 'Sin registro'
  }

  return 'Con novedad'
}

function getAvailableProgramCodes (context = {}) {
  const availableInscriptions = Array.isArray(context.availableInscriptions) ? context.availableInscriptions : []

  return availableInscriptions
    .map(inscription => getRecordValue(inscription, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']))
    .filter(Boolean)
}

function getApprovalRawStatus (approval = {}) {
  const estado = getRecordValue(approval, ['estado', 'Estado'])
  const normalizedEstado = normalizeComparable(estado)
  if (normalizedEstado) {
    return normalizedEstado
  }

  const reasonAsStatus = getRecordValue(approval, ['reason', 'Reason'])
  const normalizedReasonAsStatus = normalizeComparable(reasonAsStatus)
  if (['OK', 'PENDIENTE', 'SIN_REGISTRO'].indexOf(normalizedReasonAsStatus) !== -1) {
    return normalizedReasonAsStatus
  }

  return ''
}

function getApprovalReasonDetail (approval = {}) {
  const rawStatus = getApprovalRawStatus(approval)
  const reason = getRecordValue(approval, ['reason', 'Reason'])
  const releaseInd = getRecordValue(approval, ['releaseInd', 'ReleaseInd'])

  if (rawStatus && normalizeComparable(reason) === rawStatus && releaseInd) {
    return releaseInd
  }

  return firstNonEmpty([reason, releaseInd])
}

function getApprovalProgramCode (approval = {}, context = {}) {
  const searchableText = normalizeComparable(getRecordValue(approval, ['reason', 'Reason']))

  if (!searchableText) {
    return ''
  }

  const matchedProgramCode = getAvailableProgramCodes(context)
    .find(programCode => searchableText.indexOf(normalizeComparable(programCode)) !== -1)

  return matchedProgramCode || ''
}

function mapApprovalNote (approval) {
  const detail = getApprovalReasonDetail(approval)

  if (detail) {
    return detail
  }

  if (getApprovalRawStatus(approval) === 'SIN_REGISTRO') {
    return 'No existe un registro asociado en Banner para este aval.'
  }

  return 'Pendiente de validación por el área responsable.'
}

function mapHoldApproval (approval, index) {
  const definition = APPROVAL_DEFINITIONS[approval.holdCode] || {}

  return {
    id: definition.id || 'approval-' + (approval.holdCode || index + 1),
    name: definition.name || firstNonEmpty([approval.itemName], 'Aval institucional'),
    owner: definition.owner || firstNonEmpty([approval.itemName], 'Área responsable'),
    status: mapApprovalStatus(getApprovalRawStatus(approval)),
    updatedAt: firstNonEmpty([approval.fechaHasta, approval.fechaDesde], 'Sin fecha registrada'),
    note: mapApprovalNote(approval),
  }
}

function getProgramLevel (context = {}) {
  const sessionUser = context.sessionUser || {}
  const inscriptionStudent = context.inscriptionStudent || {}
  const inscriptionAccess = context.inscriptionAccess || {}
  const primaryProgram = getPrimaryProgram(sessionUser)

  return firstNonEmpty([
    getRecordValue(inscriptionStudent, ['typeProgram', 'TypeProgram']),
    getRecordValue(inscriptionAccess, ['typeProgram', 'TypeProgram']),
    sessionUser && sessionUser.level,
    sessionUser && sessionUser.Level,
    sessionUser && sessionUser.nivel,
    primaryProgram.level,
    primaryProgram.Level,
    primaryProgram.nivel,
  ])
}

function isPostgraduateProgram (context = {}) {
  const normalizedLevel = normalizeComparable(getProgramLevel(context))

  return normalizedLevel.indexOf('POSGRADO') !== -1 ||
    normalizedLevel.indexOf('POSTGRADO') !== -1 ||
    normalizedLevel.indexOf('ESPECIALIZ') !== -1 ||
    normalizedLevel.indexOf('MAESTR') !== -1 ||
    normalizedLevel.indexOf('DOCTOR') !== -1
}

function getSelectedProgramCode (context = {}) {
  return firstNonEmpty([
    getRecordValue(context.inscriptionStudent, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
    getRecordValue(context.inscriptionAccess, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
  ])
}

function getVisibleApprovalCodes (context = {}) {
  const holdCodes = Object.keys(APPROVAL_DEFINITIONS)

  if (!isPostgraduateProgram(context)) {
    return holdCodes
  }

  return holdCodes.filter(holdCode => POSTGRAD_HOLD_CODES_TO_HIDE.indexOf(holdCode) === -1)
}

function findProgramSpecificApproval (approvalsData = [], holdCode = '', context = {}) {
  const normalizedHoldCode = normalizeComparable(holdCode)
  const selectedProgramCode = normalizeComparable(getSelectedProgramCode(context))
  const holdApprovals = (Array.isArray(approvalsData) ? approvalsData : []).filter(approval => {
    return normalizeComparable(getRecordValue(approval, ['holdCode', 'HoldCode'])) === normalizedHoldCode
  })

  if (holdApprovals.length === 0) {
    return null
  }

  if (selectedProgramCode) {
    const exactMatch = holdApprovals.find(approval => {
      return normalizeComparable(getApprovalProgramCode(approval, context)) === selectedProgramCode
    })

    if (exactMatch) {
      return exactMatch
    }
  }

  return null
}

function buildDefaultApproval (holdCode = '', context = {}) {
  return {
    holdCode: holdCode,
    itemName: firstNonEmpty([APPROVAL_DEFINITIONS[holdCode] && APPROVAL_DEFINITIONS[holdCode].name], 'Aval institucional'),
    estado: 'PENDIENTE',
    reason: 'Pendiente de validación por el área responsable.',
    releaseInd: '',
    fechaDesde: '',
    fechaHasta: '',
    programCode: getSelectedProgramCode(context),
  }
}

function buildApprovals (approvalsData = [], context = {}) {
  return getVisibleApprovalCodes(context)
    .map((holdCode, index) => {
      return mapHoldApproval(
        findProgramSpecificApproval(approvalsData, holdCode, context) || buildDefaultApproval(holdCode, context),
        index
      )
    })
}

function findMatchingPaymentOrder (context = {}) {
  const paymentOrders = Array.isArray(context.paymentOrders) ? context.paymentOrders : []
  const inscriptionStudent = context.inscriptionStudent || {}
  const inscriptionAccess = context.inscriptionAccess || {}
  const programCode = firstNonEmpty([
    getRecordValue(inscriptionStudent, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
    getRecordValue(inscriptionAccess, ['codProgram', 'CodProgram', 'cod_Program', 'Cod_Program']),
  ])
  const programName = firstNonEmpty([
    getProgramNameFromCatalog(context, programCode),
    getRecordValue(inscriptionStudent, ['program', 'Program']),
    getRecordValue(inscriptionAccess, ['program', 'Program']),
  ])
  const programCandidates = getPaymentOrderProgramCandidates(context)

  if (!programCode) {
    return {
      programCode: '',
      programName: programName,
      order: null,
    }
  }

  const matchedOrder = paymentOrders.find(order => {
    const orderProgram = getRecordValue(order, ['programaAcademico', 'ProgramaAcademico'])
    return matchesPaymentOrderProgram(orderProgram, programCandidates)
  }) || null

  return {
    programCode: programCode,
    programName: programName,
    order: matchedOrder,
  }
}

function normalizePaymentMethodLabel (paymentMethod = '') {
  const normalizedPaymentMethod = normalizeComparable(paymentMethod)

  if (normalizedPaymentMethod === 'NOT PAID') {
    return 'Sin pago registrado'
  }

  return firstNonEmpty([paymentMethod], 'Sin información disponible')
}

function buildPaymentOrder (context = {}) {
  const match = findMatchingPaymentOrder(context)
  const matchedOrder = match.order

  if (!matchedOrder) {
    return {
      ...clone(DEFAULT_BILLING.paymentOrder),
      program: firstNonEmpty([match.programName, match.programCode]),
      note: match.programCode
        ? 'No se encontró una orden de pago en Iceberg que coincida con el código de programa ' + match.programCode + '.'
        : DEFAULT_BILLING.paymentOrder.note,
    }
  }

  const amount = Number(getRecordValue(matchedOrder, ['valorOrden', 'ValorOrden'])) || 0
  const discountAmount = Number(getRecordValue(matchedOrder, ['valorTotalDescuento', 'ValorTotalDescuento'])) || 0
  const rawPaidAmount = parseOptionalNumber(getRecordValue(matchedOrder, ['valorPagado', 'ValorPagado']))
  const paymentDate = firstNonEmpty([
    getRecordValue(matchedOrder, ['fecha', 'Fecha']),
  ], 'Sin fecha registrada')
  const paymentMethod = normalizePaymentMethodLabel(getRecordValue(matchedOrder, ['medioPago', 'MedioPago']))
  const hasPositivePaidAmount = rawPaidAmount !== null && rawPaidAmount > 0
  const isExplicitlyUnpaid = rawPaidAmount === 0 || normalizeComparable(paymentMethod) === 'SIN PAGO REGISTRADO'
  const isPaid = !isExplicitlyUnpaid && (hasPositivePaidAmount || paymentDate !== 'Sin fecha registrada')
  const paidAmount = isPaid && rawPaidAmount !== null ? rawPaidAmount : null

  return {
    status: isPaid ? 'Pagada' : 'Pendiente de pago',
    hasMatch: true,
    isPaid: isPaid,
    client: firstNonEmpty([
      getRecordValue(matchedOrder, ['cliente', 'Cliente']),
    ]),
    identification: firstNonEmpty([
      getRecordValue(matchedOrder, ['identificacion', 'Identificacion']),
    ]),
    orderNumber: firstNonEmpty([
      getRecordValue(matchedOrder, ['orden', 'Orden']),
    ]),
    document: firstNonEmpty([
      getRecordValue(matchedOrder, ['documento', 'Documento']),
    ]),
    period: firstNonEmpty([
      getRecordValue(matchedOrder, ['periodo', 'Periodo']),
    ]),
    programCode: match.programCode,
    program: firstNonEmpty([
      match.programName,
      getProgramNameFromCatalog(context, match.programCode),
      getRecordValue(context.inscriptionStudent, ['program', 'Program']),
      getRecordValue(context.inscriptionAccess, ['program', 'Program']),
      getRecordValue(matchedOrder, ['programaAcademico', 'ProgramaAcademico']),
      match.programCode,
    ]),
    amount: amount,
    discountAmount: discountAmount,
    paidAmount: paidAmount,
    updatedAt: paymentDate,
    paymentMethod: paymentMethod,
    portalUrl: isPaid ? '' : FINANCIAL_PORTAL_URL,
    note: isPaid
      ? 'El pago asociado a la orden fue registrado en el sistema para el programa ' + firstNonEmpty([
        match.programName,
        getRecordValue(context.inscriptionStudent, ['program', 'Program']),
        getRecordValue(context.inscriptionAccess, ['program', 'Program']),
        match.programCode,
      ]) + '.'
      : 'La orden de pago existe, pero aún no registra pago. El estudiante puede continuar el proceso desde el portal financiero.',
  }
}

function createState (context = {}) {
  const approvals = buildApprovals(context.approvals, context)
  const documents = buildDocuments(context.documentsSource)
  const paymentOrder = buildPaymentOrder(context)

  return {
    student: buildStudentProfile({
      ...context,
      paymentOrder: paymentOrder,
    }),
    call: buildCallData({
      ...context,
      paymentOrder: paymentOrder,
    }),
    documents: documents,
    approvals: approvals,
    billing: {
      paymentOrder: paymentOrder,
    },
    lastUpdate: formatFetchTimestamp(),
  }
}

function getRequiredDocuments (state) {
  return state.documents.filter(documentItem => documentItem.required)
}

function isDocumentApproved (documentItem) {
  const normalizedStatus = firstNonEmpty([documentItem && documentItem.status]).toUpperCase()
  return normalizedStatus === 'OK' || normalizedStatus === 'APROBADO' || normalizedStatus === 'CARGADO'
}

function getApprovedRequiredDocumentsCount (state) {
  return getRequiredDocuments(state).filter(documentItem => isDocumentApproved(documentItem)).length
}

function areRequiredDocumentsApproved (state) {
  return getRequiredDocuments(state).every(documentItem => isDocumentApproved(documentItem))
}

function getApprovedApprovalsCount (state) {
  return state.approvals.filter(approvalItem => approvalItem.status === 'Aprobado').length
}

function areApprovalsApproved (state) {
  return state.approvals.length > 0 && state.approvals.every(approvalItem => approvalItem.status === 'Aprobado')
}

function isPaymentOrderGenerated (state) {
  return Boolean(state.billing.paymentOrder.hasMatch)
}

function isPaymentOrderPaid (state) {
  return Boolean(state.billing.paymentOrder.isPaid)
}

function getCurrentStatus (state) {
  if (!areRequiredDocumentsApproved(state)) {
    return {
      label: 'En revisión de documentos',
      tone: 'info',
      description: 'Registro y Control está validando los soportes cargados por el estudiante.',
    }
  }

  if (!areApprovalsApproved(state)) {
    return {
      label: 'En revisión de avales',
      tone: 'warning',
      description: 'El proceso está a la espera de validaciones institucionales y paz y salvos.',
    }
  }

  if (!isPaymentOrderGenerated(state)) {
    return {
      label: 'Orden de pago pendiente de emisión',
      tone: 'warning',
      description: 'Aún no se encontró una orden de pago en Iceberg que coincida con el programa inscrito.',
    }
  }

  if (!isPaymentOrderPaid(state)) {
    return {
      label: 'Orden generada pendiente de pago',
      tone: 'warning',
      description: 'La orden de pago ya existe, pero todavía no registra pago en Iceberg.',
    }
  }

  return {
    label: 'Orden de pago pagada',
    tone: 'success',
    description: 'La orden de pago del proceso ya registra pago en Iceberg.',
  }
}

function buildNextStep (state) {
  if (isPaymentOrderPaid(state)) {
    return {
      title: 'Proceso financiero al día',
      description: 'La orden de pago ya fue cancelada para el programa ' + state.billing.paymentOrder.program + '.',
    }
  }

  if (isPaymentOrderGenerated(state)) {
    return {
      title: 'Pagar en portal financiero',
      description: 'La orden ' + state.billing.paymentOrder.orderNumber + ' está pendiente y puede pagarse desde el portal financiero.',
    }
  }

  return {
    title: 'Pendiente emisión de la orden de pago',
    description: 'La orden será visible cuando Tesorería complete la liquidación de derechos de grado.',
  }
}

function getLastApprovalUpdate (state) {
  const withDate = state.approvals.filter(item => item.updatedAt && item.updatedAt !== 'Sin fecha registrada')
  return withDate.length > 0 ? withDate[withDate.length - 1].updatedAt : 'Sin fecha registrada'
}

function getLastDocumentUpdate (state) {
  const withDate = state.documents.filter(item => item && item.updatedAt && item.updatedAt !== 'Sin fecha registrada')
  return withDate.length > 0 ? withDate[withDate.length - 1].updatedAt : state.call.registrationDate || 'Sin fecha registrada'
}

function buildTimeline (state) {
  const documentsReady = areRequiredDocumentsApproved(state)
  const approvalsReady = areApprovalsApproved(state)
  const paymentGenerated = isPaymentOrderGenerated(state)
  const paymentPaid = isPaymentOrderPaid(state)

  return [
    {
      id: 'registration',
      title: 'Inscripción confirmada',
      description: 'Registro de participación en la convocatoria de grados.',
      date: state.call.registrationDate,
      status: state.call.registrationDate ? 'completed' : 'current',
    },
    {
      id: 'documents',
      title: 'Cargue de documentos',
      description: documentsReady
        ? state.documents.length > 0
          ? 'Registro y control se comunicará en caso de tener alguna novedad con sus documentos.'
          : 'No se requieren documentos adicionales para esta inscripcion.'
        : 'Faltan validaciones sobre los soportes cargados.',
      date: getLastDocumentUpdate(state),
      status: !state.call.registrationDate ? 'upcoming' : documentsReady ? 'completed' : 'current',
    },
    {
      id: 'approvals',
      title: 'Validación de avales',
      description: approvalsReady
        ? 'Las áreas institucionales emitieron sus avales.'
        : 'Continúa pendiente la emisión de paz y salvos y avales internos.',
      date: getLastApprovalUpdate(state),
      status: !documentsReady ? 'upcoming' : approvalsReady ? 'completed' : 'current',
    },
    {
      id: 'payment',
      title: 'Orden de pago',
      description: paymentPaid
        ? 'La orden de pago ya registra pago en Iceberg.'
        : paymentGenerated
          ? 'La orden de pago ya fue emitida, pero sigue pendiente de pago.'
          : 'La orden de pago aún no aparece registrada para el programa del estudiante.',
      date: state.billing.paymentOrder.updatedAt || 'Sin fecha registrada',
      status: !approvalsReady ? 'upcoming' : paymentPaid ? 'completed' : 'current',
    },
  ]
}

function buildSummaryNotes (state) {
  const notes = [
    'La convocatoria actual se desarrolla bajo la parametrización institucional del periodo ' + state.call.period + '.',
  ]

  if (state.documents.length === 0) {
    notes.push('No se encontraron documentos obligatorios asociados a la inscripcion activa.')
  } else if (areRequiredDocumentsApproved(state)) {
    notes.push('Los documentos requeridos ya se encuentran registrados con estado CARGADO.')
  } else {
    notes.push('Todavía hay documentos obligatorios en validación.')
  }

  if (state.approvals.length === 0) {
    notes.push('No se encontraron avales registrados en Banner para el estudiante logeado.')
  } else if (areApprovalsApproved(state)) {
    notes.push('Los avales institucionales ya están completos.')
  } else {
    notes.push('Hay avales pendientes por emitir desde las áreas internas.')
  }

  if (isPaymentOrderGenerated(state)) {
    notes.push(
      isPaymentOrderPaid(state)
        ? 'La orden de pago ya fue encontrada y registra pago para el programa ' + state.billing.paymentOrder.program + '.'
        : 'La orden de pago ya fue encontrada para el programa ' + state.billing.paymentOrder.program + ', pero aún no registra pago.'
    )
  } else {
    notes.push('Todavía no se encontró una orden de pago asociada al código de programa inscrito.')
  }

  return notes
}

function buildSummaryCounters (state) {
  return [
    {
      id: 'documents',
      label: 'Documentos requeridos',
      value: getApprovedRequiredDocumentsCount(state) + '/' + getRequiredDocuments(state).length,
      detail: state.documents.length > 0
        ? 'Documentos obligatorios reportados desde la inscripcion activa'
        : 'Sin documentos obligatorios registrados para esta inscripcion',
    },
    {
      id: 'approvals',
      label: 'Avales emitidos',
      value: getApprovedApprovalsCount(state) + '/' + state.approvals.length,
      detail: state.approvals.length > 0
        ? 'Información consolidada desde las áreas responsables'
        : 'Sin registros de avales cargados desde Banner',
    },
    {
      id: 'payment-order',
      label: 'Orden de pago',
      value: state.billing.paymentOrder.status,
      detail: state.billing.paymentOrder.orderNumber || 'Sin coincidencia por programa',
    },
    {
      id: 'payment-program',
      label: 'Programa',
      value: state.billing.paymentOrder.program || 'Pendiente',
      detail: state.billing.paymentOrder.hasMatch
        ? 'Coincide con el código ' + state.billing.paymentOrder.programCode + ' del programa inscrito'
        : 'No existe una orden que coincida con el código del programa',
    },
  ]
}

function buildSummary (state) {
  const currentStatus = getCurrentStatus(state)
  const nextStep = buildNextStep(state)

  return {
    student: clone(state.student),
    call: clone(state.call),
    lastUpdate: state.lastUpdate,
    status: currentStatus.label,
    statusTone: currentStatus.tone,
    statusDescription: currentStatus.description,
    nextStepTitle: nextStep.title,
    nextStepDescription: nextStep.description,
    counters: buildSummaryCounters(state),
    notes: buildSummaryNotes(state),
    timeline: buildTimeline(state),
  }
}

function buildBillingPayload (state) {
  const enabled = isPaymentOrderGenerated(state)

  return {
    enabled: enabled,
    helperText: enabled
      ? state.billing.paymentOrder.isPaid
        ? 'La orden de pago del proceso ya registra pago.'
        : 'La orden de pago del proceso está disponible y pendiente de pago.'
      : 'No se encontró una orden de pago que coincida con el programa inscrito.',
    paymentOrder: {
      status: state.billing.paymentOrder.status,
      isPaid: state.billing.paymentOrder.isPaid,
      client: state.billing.paymentOrder.client,
      identification: state.billing.paymentOrder.identification,
      orderNumber: state.billing.paymentOrder.orderNumber,
      document: state.billing.paymentOrder.document,
      period: state.billing.paymentOrder.period,
      programCode: state.billing.paymentOrder.programCode,
      program: state.billing.paymentOrder.program,
      amount: state.billing.paymentOrder.amount,
      amountLabel: formatCurrency(state.billing.paymentOrder.amount),
      discountAmount: state.billing.paymentOrder.discountAmount,
      discountAmountLabel: formatCurrency(state.billing.paymentOrder.discountAmount),
      paidAmount: state.billing.paymentOrder.paidAmount,
      paidAmountLabel: state.billing.paymentOrder.paidAmount === null ? '' : formatCurrency(state.billing.paymentOrder.paidAmount),
      updatedAt: state.billing.paymentOrder.updatedAt,
      paymentMethod: state.billing.paymentOrder.paymentMethod,
      portalUrl: state.billing.paymentOrder.portalUrl,
      note: state.billing.paymentOrder.note,
    },
  }
}

export function getGraduationProcessSummary (context = {}) {
  const state = createState(context)
  return Promise.resolve(buildSummary(state))
}

export function getGraduationDocuments (documentsSource = {}) {
  return Promise.resolve(buildDocuments(documentsSource))
}

export function getGraduationApprovals (approvals = [], context = {}) {
  return Promise.resolve(buildApprovals(approvals, context))
}

export function getGraduationBilling (context = {}) {
  const state = createState(context)
  return Promise.resolve(buildBillingPayload(state))
}
